import { Injectable } from '@angular/core';
import { Observable, forkJoin } from 'rxjs';
import { map } from 'rxjs/operators';
import { TicketClientApiService } from 'src/app/core/service/apis/tickets/ticket-client-api.service';
import { TicketMapperService } from 'src/app/shared/service/ticket-client/tickets-mapper.service';
import { ActorType, Ticket, TicketCategory } from 'src/app/shared/models/tickets/ticket.model';
import { TicketNotaryRepository } from '../../domain/repository/ticket-notary.repository';
import { TicketNotaryRegisterPayload } from '../../domain/models/ticket-notary.model';
import { DocumentApiService } from 'src/app/core/service/apis/files/document-api.service';
import { DOMAIN_TYPE_SUPPORT } from 'src/app/shared/models/documents/documents.model';
import { AttachmentType } from 'src/app/shared/constants/attachment-type.constants';
import { TicketFieldUpdateService } from 'src/app/shared/service/ticket-client/ticket-field-update.service';

@Injectable()
export class HttpTicketNotaryRepository implements TicketNotaryRepository {
  constructor(
    private readonly ticketsApi: TicketClientApiService,
    private readonly mapper: TicketMapperService,
    private readonly documentApi: DocumentApiService,
    private readonly fieldUpdate: TicketFieldUpdateService,
  ) { }

  getDetail(recordId: string): Observable<Ticket | null> {
    return forkJoin({
      detail: this.ticketsApi.getTicketDetail(recordId),
      docs: this.searchDocs(recordId, AttachmentType.ADJUNTOS),
      clientDocs: this.searchDocs(recordId, AttachmentType.RESPUESTA_USUARIO),
    }).pipe(
      map(({ detail, docs, clientDocs }) => (detail?.ticketRecordId
        ? {
          ...this.mapper.mapTicketDetail(detail, 'notary'),
          attachedDocument: this.mapper.mapTicketDocuments(docs),
          clientAttachedDocument: this.mapper.mapTicketDocuments(clientDocs),
        }
        : null)),
    );
  }

  private searchDocs(recordId: string, attachmentType: AttachmentType) {
    return this.documentApi.searchDocuments({ domainType: DOMAIN_TYPE_SUPPORT, recordId, attachmentType });
  }

  register(payload: TicketNotaryRegisterPayload): Observable<{ recordIdTicket: string }> {
    return this.ticketsApi.createTicket({
      recordIdConstitucion: payload.constitutionRecordId,
      tipoIncidencia: [this.mapper.reverseMapIncidentType(payload.incidentType)],
      descripcion: payload.description,
      categoriaTicket: TicketCategory.NOTARY,
      actorCreador: ActorType.CC_AGENT,
      ...(payload.constitutionStatus && { metadata: { estadoDeCreacionNotariza: payload.constitutionStatus } }),
    }).pipe(
      map(response => ({ recordIdTicket: response.ticketRecordId })),
    );
  }

  updateStringField(recordId: string, field: string, value: any): Observable<void> {
    return this.fieldUpdate.updateField(recordId, field, value);
  }

  updateStatus(recordId: string, newStatus: string, actorType: string, reportedBy: string): Observable<void> {
    return this.ticketsApi.updateTicketStatus(recordId, {
      newStatus,
      reportedBy,
      actionType: 'STATUS_CHANGE',
      actorType,
    }).pipe(
      map(() => undefined),
    );
  }

}
